
import React, { useEffect } from 'react';

interface SEOMetadataProps {
  title?: string;
  description?: string;
  imageUrl?: string | null;
  creator?: string;
  defaultTitle: string;
}

export function SEOMetadata({ 
  title, 
  description, 
  imageUrl, 
  creator,
  defaultTitle
}: SEOMetadataProps) {
  const pageTitle = title || defaultTitle;
  const metaDescription = description || `${pageTitle} - Learn from ${creator}`;
  
  useEffect(() => {
    // Update document title
    document.title = pageTitle;
    
    // Update meta description
    let metaDescElement = document.querySelector('meta[name="description"]');
    if (!metaDescElement) {
      metaDescElement = document.createElement('meta');
      metaDescElement.setAttribute('name', 'description');
      document.head.appendChild(metaDescElement);
    }
    metaDescElement.setAttribute('content', metaDescription);
    
    // OpenGraph meta tags
    updateMetaTag('property', 'og:type', 'website');
    updateMetaTag('property', 'og:title', pageTitle);
    updateMetaTag('property', 'og:description', metaDescription);
    if (imageUrl) {
      updateMetaTag('property', 'og:image', imageUrl);
    }
    
    // Twitter meta tags
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', pageTitle);
    updateMetaTag('name', 'twitter:description', metaDescription);
    if (imageUrl) {
      updateMetaTag('name', 'twitter:image', imageUrl);
    }
    
    // Add structured data for courses
    if (creator) {
      const courseSchema = {
        '@context': 'https://schema.org',
        '@type': 'Course',
        'name': pageTitle,
        'description': metaDescription,
        'provider': {
          '@type': 'Person',
          'name': creator
        }
      };
      
      let scriptElement = document.querySelector('#course-schema') as HTMLScriptElement | null;
      if (scriptElement) {
        document.head.removeChild(scriptElement);
      }
      
      scriptElement = document.createElement('script');
      scriptElement.id = 'course-schema';
      scriptElement.type = 'application/ld+json';
      scriptElement.text = JSON.stringify(courseSchema);
      document.head.appendChild(scriptElement);
    }
    
    return () => {
      // Clean up structured data when component unmounts
      const scriptElement = document.querySelector('#course-schema');
      if (scriptElement) {
        document.head.removeChild(scriptElement);
      }
    };
  }, [pageTitle, metaDescription, imageUrl, creator]);
  
  // Helper function to create or update meta tags
  const updateMetaTag = (attributeName: string, attributeValue: string, content: string) => {
    let metaTag = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute(attributeName, attributeValue);
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', content);
  };
  
  // This component doesn't render anything visible
  return null;
}
